'use client'

import { useTransition } from 'react'
import { Formik, Form } from 'formik'
import { TextField, TextFieldTypes } from 'sidecar-components/atoms/TextField'
import {
  Button,
  ButtonSize,
  ButtonSentiment,
} from 'sidecar-components/atoms/Button'
import { Card } from 'sidecar-components/atoms/Card'
import { FormErrorBubble } from './FormErrorBubble'
import {
  otpCodeValidationSchema,
  type OTPCodeFormValues,
} from '@/lib/validation-schemas'

/**
 * OTPStepForm component for the second step of login flow.
 *
 * Displays a 6-digit OTP verification code input field. This is the second
 * factor of the two-factor authentication flow.
 */
interface OTPStepFormProps {
  email: string
  /**
   * Cryptographic token proving password auth was completed (required for OTP
   * verification)
   */
  pendingAuthToken: string
  onSubmit: (formData: FormData) => void
  isSubmitting: boolean
  error?: string
  onResend: () => void
  isResendDisabled: boolean
  resendCooldown: number
  resendError?: string
  isResendPending?: boolean
}

export function OTPStepForm({
  email,
  pendingAuthToken,
  onSubmit,
  isSubmitting,
  error,
  onResend,
  isResendDisabled,
  resendCooldown,
  resendError,
  isResendPending,
}: OTPStepFormProps) {
  // useTransition for proper server action handling
  const [isPending, startTransition] = useTransition()

  return (
    <Card
      className="relative z-1"
      childrenClass="p-2"
      footer={
        // Resend code link with cooldown timer
        <p className="text-center">
          <strong>Didn{`\u2019`}t receive the email?</strong>{' '}
          <Button
            sentiment={ButtonSentiment.LINK}
            size={ButtonSize.LARGE}
            onClick={onResend}
            disabled={isResendDisabled}
          >
            {resendCooldown > 0
              ? `Resend (${resendCooldown}s)`
              : isResendPending
                ? 'Sending...'
                : 'Resend'}
          </Button>
        </p>
      }
    >
      <>
        {/* Header with user's email */}
        <div className="mb-3">
          <h2>Verify your email address</h2>
          <p>
            We just emailed a 6-digit code to{' '}
            <strong data-heap-redact-text="true">{email}</strong>. Enter it
            below to verify — it expires in 10 minutes.
          </p>
        </div>

        {/* Error message from OTP verification */}
        <FormErrorBubble
          error={error}
          isLoading={isSubmitting}
          testId="otp-error"
        />

        {/* Error message from resend action */}
        <FormErrorBubble
          error={resendError}
          isLoading={isResendPending}
          testId="resend-error"
        />

        {/* Formik form for OTP code */}
        <Formik<OTPCodeFormValues>
          initialValues={{ code: '' }}
          validationSchema={otpCodeValidationSchema}
          onSubmit={(values, { setSubmitting }) => {
            const formData = new FormData()
            formData.append('email', email)
            formData.append('code', values.code)
            formData.append('pendingAuthToken', pendingAuthToken)

            startTransition(() => {
              onSubmit(formData)
              setSubmitting(false)
            })
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
            handleBlur,
            isSubmitting: formikSubmitting,
          }) => {
            // Determine if submit button should be disabled
            const isButtonDisabled =
              !values.code.trim() ||
              !!errors.code ||
              isSubmitting ||
              formikSubmitting ||
              isPending

            return (
              <Form onSubmit={handleSubmit}>
                {/* Verification code field with numeric filtering */}
                <TextField
                  label="Verification code"
                  name="code"
                  type={TextFieldTypes.TEXT}
                  value={values.code}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    // Only allow numbers and limit to 6 digits
                    const numericValue = e.target.value
                      .replace(/\D/g, '')
                      .slice(0, 6)

                    handleChange({
                      ...e,
                      target: {
                        ...e.target,
                        name: 'code',
                        value: numericValue,
                      },
                    })
                  }}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  placeholder=""
                  testId="input_verificationCode"
                  className="mb-8"
                />

                {/* Submit button */}
                <Button
                  type="submit"
                  size={ButtonSize.LARGE}
                  width="full"
                  disabled={isButtonDisabled}
                  busy={isSubmitting || formikSubmitting}
                  icon="forward"
                  iconPosition="after"
                  testId="btn_verify"
                >
                  {isSubmitting || formikSubmitting ? 'Verifying...' : 'Verify'}
                </Button>
              </Form>
            )
          }}
        </Formik>
      </>
    </Card>
  )
}
